import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styles from './BonusVideos.module.scss';
import VideoPreviewDialog from '../../shared/VideoPreviewDialog';
import VideoCard from '../../shared/VideoCard';
import Loader from '../../shared/Loader';
import { AddIcon } from '../../icons';
import { FreeVideoDialog } from '../../shared/VideoDialog';
import ConfirmDialog from '../../shared/ConfirmDialog';
import { useFreeVideo } from '../../../redux/hooks';
import { useVideoProcessingStatus } from '../../../hooks/useVideoProcessingStatus';
import { ITraining } from '../../../types';

const FreeVideo: React.FC = () => {
    const { t } = useTranslation();
    const [newVideoIds, setNewVideoIds] = useState<Set<number>>(new Set());

    const {
        video,
        hasVideo,
        isLoading,
        selectedVideo,
        openDialog,
        deleteDialog,
        loadFreeVideo,
        handleSubmit,
        handleDelete,
        confirmDelete,
        handleVideoClick,
        closeVideoPreview,
        handleAddVideo,
        closeDialog,
        closeDeleteDialog,
        updateVideoFromStatus
    } = useFreeVideo();

    useEffect(() => {
        loadFreeVideo();
    }, [loadFreeVideo]);

    // Хук для отслеживания статуса обработки видео (если есть видео)
    const { status: processingStatus, message: processingMessage } = useVideoProcessingStatus({
        videoId: video?.id || 0,
        autoRefresh: !!video?.id,
        refreshInterval: 3000,
        onStatusChange: (status, data) => {
            // Обновляем видео в Redux при получении новых данных
            if (video && updateVideoFromStatus && data) {
                const updates: Partial<ITraining> = {};

                // Обновляем video_url если получили
                if (data.url && data.url !== (typeof video.video_url === 'string' ? video.video_url : '')) {
                    (updates as any).video_url = data.url;
                }

                // Обновляем preview_url если получили
                if (data.preview_url && data.preview_url !== (typeof video.preview_url === 'string' ? video.preview_url : '')) {
                    (updates as any).preview_url = data.preview_url;
                }

                // Обновляем title если получили
                if (data.title && data.title !== video.title) {
                    updates.title = data.title;
                }

                // Если есть обновления, применяем их
                if (Object.keys(updates).length > 0) {
                    updateVideoFromStatus(video.id, updates);

                    // Если получили video_url или preview_url, убираем видео из списка новых
                    if ((updates.video_url || updates.preview_url)) {
                        setNewVideoIds(prev => {
                            const newSet = new Set(prev);
                            newSet.delete(video.id);
                            return newSet;
                        });
                    }
                }
            }
        }
    });

    // Обертка для handleSubmit с отслеживанием новых видео
    const handleVideoSubmit = async (data: any) => {
        try {
            const result = await handleSubmit(data);

            // Если добавление прошло успешно и мы получили ID нового видео
            if (result && typeof result === 'object' && 'type' in result && result.type.endsWith('/fulfilled') && 'payload' in result && result.payload && typeof result.payload === 'object' && 'id' in result.payload) {
                const videoId = (result.payload as any).id;
                setNewVideoIds(prev => new Set(prev).add(videoId));

                // Убираем ID из списка новых через некоторое время
                setTimeout(() => {
                    setNewVideoIds(prev => {
                        const newSet = new Set(prev);
                        newSet.delete(videoId);
                        return newSet;
                    });
                }, 30000); // 30 секунд на обработку
            }
        } catch (error) {
            console.error('Ошибка при добавлении бесплатного видео:', error);
        }
    };

    return (
        <div className={styles.container}>
            <header className={styles.header}>
                <h1 className={styles.title}>
                    {t('content.freeVideo.title')}
                </h1>
                <div className={styles.buttonGroup}>
                    <button
                        className={`${styles.button} ${styles.buttonPrimary}`}
                        onClick={handleAddVideo}
                    >
                        <AddIcon /> {hasVideo ? t('content.freeVideo.changeButton') : t('content.freeVideo.addButton')}
                    </button>
                </div>
            </header>

            {isLoading ? (
                <div className={styles.emptyState}>
                    <Loader fullScreen={false} />
                </div>
            ) : hasVideo ? (
                <div className={styles.videoContainer}>
                    <VideoCard
                        key={video!.id}
                        id={video!.id}
                        title=""
                        thumbnailUrl={typeof video!.preview_url === 'string' ? video!.preview_url : ''}
                        videoUrl={typeof video!.video_url === 'string' ? video!.video_url : ''}
                        processingStatus={processingStatus}
                        processingMessage={processingMessage}
                        onEdit={handleAddVideo}
                        onDelete={() => handleDelete(video!.id)}
                        onClick={() => handleVideoClick(video!)}
                        hideTitle
                        horizontal={true}
                        showProcessingIndicator={processingStatus !== 'completed'}
                        isNewVideo={newVideoIds.has(video!.id)}
                    />
                </div>
            ) : (
                <div className={styles.emptyState}>
                    <p className={styles.emptyStateText}>{t('content.freeVideo.noVideo')}</p>
                </div>
            )}

            <FreeVideoDialog
                open={openDialog}
                onClose={closeDialog}
                onSubmit={handleVideoSubmit}
                dialogTitle={t('content.freeVideo.uploadDialog.title')}
                initialData={video ? {
                    videoType: video.video_type as 'youtube' | 'file',
                    youtubeUrl: video.youtube_url,
                    language: video.language as 'ru' | 'it',
                } : undefined}
            />

            <VideoPreviewDialog
                open={!!selectedVideo}
                onClose={closeVideoPreview}
                title=""
                videoUrl={selectedVideo?.youtube_url || (typeof selectedVideo?.video_url === 'string' ? selectedVideo.video_url : '')}
                isYouTube={selectedVideo?.video_type === 'youtube'}
            />

            <ConfirmDialog
                open={deleteDialog.open}
                title={t('content.freeVideo.deleteDialog.title')}
                message={t('content.freeVideo.deleteDialog.message')}
                onConfirm={confirmDelete}
                onCancel={closeDeleteDialog}
            />
        </div>
    );
};

export default FreeVideo; 